import { Alert, Badge, Box, Button, Group, Loader, Menu, Paper, ScrollArea, Stack, Text, Title, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerFilter } from "@msc/shared";

import { CustomerPicker } from "../components/customerView/CustomerPicker.js";
import { PrepReview } from "../components/customerView/PrepReview.js";
import { PresentPrep } from "../components/customerView/PresentPrep.js";
import { PresenterCockpit } from "../components/customerView/PresenterCockpit.js";
import { ReconnectBanner } from "../components/customerView/ReconnectBanner.js";
import { TicketDetail } from "../components/customerView/TicketDetail.js";
import { TicketList } from "../components/customerView/TicketList.js";
import { TicketToolbar } from "../components/customerView/TicketToolbar.js";
import { useFillViewport } from "../hooks/useFillViewport.js";
import {
  isJiraReauthRequired,
  useCustomers,
  useCustomerViewConnection,
  useDisconnectJira,
  useTickets,
} from "../hooks/useCustomerView.js";
import { differsFromEnvironment } from "../lib/customerView/site.js";
import { applyTicketView, DEFAULT_VIEW, groupingForPrep, orderedTickets, type GroupKey, type SortKey, type TicketView } from "../lib/customerView/ticketView.js";
import { usePresentationStore } from "../stores/presentation.js";
import { isNewSinceLastMeeting, useReviewedStore } from "../stores/reviewed.js";
import { ApiError } from "../lib/api.js";

// ── Search-Param Validation ─────────────────────────────────────────────────

const SORT_KEYS: SortKey[] = ["updated", "created", "status", "type", "key"];
const GROUP_KEYS: GroupKey[] = ["none", "status", "type", "reporter"];

export interface CustomerViewSearch {
  /** Fehlercode aus dem OAuth-Callback (Redirect). */
  verbindungsfehler?: string;
  /** objectKey der Kundenabteilung, z. B. KS-6951. */
  kunde?: string;
  /** aktives Ticket, z. B. MS-1042. */
  ticket?: string;
  filter?: CustomerFilter;
  q?: string;
  sort?: SortKey;
  gruppe?: GroupKey;
  /** ausgeschlossene Status */
  status?: string[];
  /** ausgeschlossene Typen */
  typ?: string[];
  /** ausgewählte Ersteller */
  ersteller?: string[];
  /** ausgewählte Bearbeiter; `-` steht für „nicht zugewiesen" */
  bearbeiter?: string[];
  /** Fokus-Modus: Liste eingeklappt */
  fokus?: boolean;
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v.length > 0 ? v : undefined);
const strList = (v: unknown): string[] | undefined =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === "string") ? (v as string[]) : undefined;

export function validateCustomerViewSearch(raw: Record<string, unknown>): CustomerViewSearch {
  const out: CustomerViewSearch = {};
  const v = str(raw["verbindungsfehler"]); if (v) out.verbindungsfehler = v;
  const kunde = str(raw["kunde"]); if (kunde) out.kunde = kunde;
  const ticket = str(raw["ticket"]); if (ticket) out.ticket = ticket;
  if (raw["filter"] === "mine" || raw["filter"] === "all") out.filter = raw["filter"];
  const q = str(raw["q"]); if (q) out.q = q;
  if (SORT_KEYS.includes(raw["sort"] as SortKey)) out.sort = raw["sort"] as SortKey;
  if (GROUP_KEYS.includes(raw["gruppe"] as GroupKey)) out.gruppe = raw["gruppe"] as GroupKey;
  const status = strList(raw["status"]); if (status) out.status = status;
  const typ = strList(raw["typ"]); if (typ) out.typ = typ;
  const ersteller = strList(raw["ersteller"]); if (ersteller) out.ersteller = ersteller;
  // Bis #235 ein einzelner Wert — ein altes Lesezeichen `?bearbeiter=Name`
  // soll weiter tragen, deshalb wird auch die Einzelform angenommen.
  const bearbeiter = strList(raw["bearbeiter"]) ?? (str(raw["bearbeiter"]) ? [raw["bearbeiter"] as string] : undefined);
  if (bearbeiter) out.bearbeiter = bearbeiter;
  if (raw["fokus"] === true) out.fokus = true;
  return out;
}

const LAST_CUSTOMER_KEY = "msc-customer-view-last-customer";
function loadLastCustomer(): string | null {
  try { return localStorage.getItem(LAST_CUSTOMER_KEY); } catch { return null; }
}
function saveLastCustomer(key: string): void {
  try { localStorage.setItem(LAST_CUSTOMER_KEY, key); } catch { /* egal */ }
}

// ── Seite ───────────────────────────────────────────────────────────────────

/**
 * Die Jira-Site, mit der die Customer View für diesen Lead arbeitet. Sie folgt
 * seinem Token, nicht der Serverkonfiguration — sichtbar, weil ein Lead auf
 * Staging sonst nicht merkt, dass er in echten Prod-Tickets steht. Gefärbt
 * wird nicht „ist es Sandbox", sondern der riskante Fall: Der Host weicht von
 * der Umgebung ab, in der dieses CRM läuft (`configuredHost`).
 *
 * Auf der Verbinden-Seite gibt es noch keine Site aus dem Token; dort zeigt
 * dasselbe Abzeichen die Site der **Umgebung** — mit eigenem `hint`, weil der
 * Standardtext („aus deiner Verbindung") dann eine Verbindung behauptete, die
 * es noch nicht gibt.
 */
function SiteBadge({ host, configuredHost, hint }: { host: string; configuredHost: string | null; hint?: string }) {
  const { t } = useTranslation();
  const differs = differsFromEnvironment(host, configuredHost);
  return (
    <Tooltip label={hint ?? t("pages.customerView.siteHint")}>
      <Badge size="sm" variant="light" color={differs ? "orange" : "gray"}>
        {differs ? `${t("pages.customerView.siteDiffers")} · ${host}` : host}
      </Badge>
    </Tooltip>
  );
}

/**
 * Die Site ist gerade nicht bestimmbar (`SITE_UNKNOWN`) — ein Ausfall bei
 * Atlassian, kein Auftrag an den Lead. Grau und benannt statt gar nichts: Eine
 * leere Stelle, wo sonst der Host steht, liest sich wie „alles normal".
 */
function SiteUnknownBadge() {
  const { t } = useTranslation();
  return (
    <Tooltip label={t("pages.customerView.siteUnknownHint")}>
      <Badge size="sm" variant="light" color="gray">{t("pages.customerView.siteUnknown")}</Badge>
    </Tooltip>
  );
}

export function CustomerViewPage() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as CustomerViewSearch;
  const connection = useCustomerViewConnection();
  const disconnect = useDisconnectJira();

  const fehlerText = (code: string) => t([`pages.customerView.errors.${code}`, "pages.customerView.errors.unbekannt"]);

  return (
    <Stack gap="md" h="100%">
      <Group justify="space-between" align="center">
        <Title order={2}>{t("pages.customerView.title")}</Title>
        {connection.data?.connected && (
          <Menu>
            <Menu.Target><Button size="xs" variant="subtle">{t("pages.customerView.cockpit.jiraMenu")}</Button></Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{t("pages.customerView.connectedAs", { accountId: connection.data.jiraAccountId })}</Menu.Label>
              <Menu.Item
                onClick={() => disconnect.mutate(undefined, {
                  onSuccess: () => notifications.show({ message: t("pages.customerView.disconnected") }),
                  onError: (err) => notifications.show({ color: "red", message: err instanceof ApiError ? err.message : t("pages.customerView.disconnectError") }),
                })}
              >
                {t("pages.customerView.disconnect")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {search.verbindungsfehler && (
        <Alert color="red" title={t("pages.customerView.errorTitle")}>{fehlerText(search.verbindungsfehler)}</Alert>
      )}
      {connection.isError && (
        <Alert color="red" title={t("pages.customerView.loadErrorTitle")}>
          {connection.error instanceof ApiError ? connection.error.message : t("pages.customerView.loadError")}
        </Alert>
      )}
      {connection.isLoading && <Loader />}
      {connection.data && !connection.data.configured && (
        <Alert color="yellow" title={t("pages.customerView.notConfiguredTitle")}>{t("pages.customerView.notConfigured")}</Alert>
      )}
      {connection.data?.configured && !connection.data.connected && (
        <Paper withBorder p="lg">
          <Stack gap="sm">
            <Text>{t("pages.customerView.connectIntro")}</Text>
            <Group gap="xs" align="center">
              <Text size="sm" c="dimmed">{t("pages.customerView.connect.siteChoice")}</Text>
              {/* Hier gibt es noch keine Verbindung, also auch keine Site aus
                  dem Token — gezeigt wird die Site der Umgebung, damit der Lead
                  weiß, welche er bei Atlassian erwartungsgemäß auswählt. */}
              {connection.data.configuredHost && (
                <SiteBadge
                  host={connection.data.configuredHost}
                  configuredHost={connection.data.configuredHost}
                  hint={t("pages.customerView.connect.environmentHint")}
                />
              )}
            </Group>
            <Group><Button component="a" href="/api/v1/customer-view/connect/login">{t("pages.customerView.connectButton")}</Button></Group>
          </Stack>
        </Paper>
      )}
      {connection.data?.connected && <Cockpit search={search} />}
    </Stack>
  );
}

function Cockpit({ search }: { search: CustomerViewSearch }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const filter: CustomerFilter = search.filter ?? "mine";
  const customerKey = search.kunde ?? null;
  const focusMode = search.fokus === true;

  const view: TicketView = useMemo(() => ({
    q: search.q ?? DEFAULT_VIEW.q,
    excludedStatus: search.status ?? DEFAULT_VIEW.excludedStatus,
    excludedTypes: search.typ ?? DEFAULT_VIEW.excludedTypes,
    reporters: search.ersteller ?? DEFAULT_VIEW.reporters,
    assignees: search.bearbeiter ?? DEFAULT_VIEW.assignees,
    sort: search.sort ?? DEFAULT_VIEW.sort,
    group: search.gruppe ?? DEFAULT_VIEW.group,
  }), [search]);

  /** Suchparameter ändern, ohne die übrigen zu verlieren. */
  const patch = (next: Partial<CustomerViewSearch>) =>
    navigate({
      to: "/customer-view",
      search: (prev): CustomerViewSearch => ({ ...(prev as CustomerViewSearch), ...next }),
      replace: true,
    });

  const setView = (v: TicketView) =>
    void patch({
      q: v.q || undefined,
      status: v.excludedStatus.length ? v.excludedStatus : undefined,
      typ: v.excludedTypes.length ? v.excludedTypes : undefined,
      ersteller: v.reporters.length ? v.reporters : undefined,
      bearbeiter: v.assignees.length ? v.assignees : undefined,
      sort: v.sort === DEFAULT_VIEW.sort ? undefined : v.sort,
      gruppe: v.group === DEFAULT_VIEW.group ? undefined : v.group,
    });

  const customers = useCustomers(filter);
  const tickets = useTickets(customerKey);

  // Präsentationsmodus: Ab `prep` gehört die Fläche der Vorbereitung, nicht
  // mehr Liste und Detail.
  const phase = usePresentationStore((s) => s.phase);
  const startPrep = usePresentationStore((s) => s.startPrep);

  // „Besprochen"-Haken liegen je Kundenabteilung in localStorage.
  const loadReviewedCustomer = useReviewedStore((s) => s.loadCustomer);
  const reviewedAt = useReviewedStore((s) => s.reviewedAt);
  const toggleReviewed = useReviewedStore((s) => s.toggle);
  useEffect(() => {
    loadReviewedCustomer(customerKey);
  }, [customerKey, loadReviewedCustomer]);

  // Kunde vorbelegen: zuletzt geöffneter, sonst der erste eigene.
  useEffect(() => {
    if (customerKey || !customers.data || customers.data.length === 0) return;
    const last = loadLastCustomer();
    const pick = last && customers.data.some((c) => c.objectKey === last) ? last : filter === "mine" ? customers.data[0]!.objectKey : null;
    if (pick) void patch({ kunde: pick });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerKey, customers.data, filter]);

  const result = useMemo(() => applyTicketView(tickets.data ?? [], view), [tickets.data, view]);
  // Navigation folgt der gerenderten Reihenfolge (mit Gruppierung die
  // Gruppenreihenfolge), nicht der flachen `visible`-Liste — die zählt nur
  // für die Anzahl und den `TicketList`-Prop.
  const ordered = useMemo(() => orderedTickets(result), [result]);
  const selectedCustomer = customers.data?.find((c) => c.objectKey === customerKey) ?? null;
  const reviewedKeys = useMemo(() => new Set(Object.keys(reviewedAt)), [reviewedAt]);
  const newKeys = useMemo(
    () => new Set(result.visible.filter((x) => isNewSinceLastMeeting(x, reviewedAt)).map((x) => x.key)),
    [result.visible, reviewedAt],
  );
  const activeKey = search.ticket ?? null;
  const activeIndex = ordered.findIndex((x) => x.key === activeKey);
  const activeTicket = activeIndex >= 0 ? ordered[activeIndex]! : null;

  // Erstes sichtbares Ticket wählen, wenn keins aktiv oder das aktive weggefiltert ist.
  useEffect(() => {
    if (!tickets.data) return;
    if (result.visible.length === 0) { if (activeKey) void patch({ ticket: undefined }); return; }
    if (activeIndex < 0) void patch({ ticket: ordered[0]!.key });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.visible, ordered, activeKey, activeIndex, tickets.data]);

  const goPrev = () => { if (activeIndex > 0) void patch({ ticket: ordered[activeIndex - 1]!.key }); };
  const goNext = () => { if (activeIndex >= 0 && activeIndex < ordered.length - 1) void patch({ ticket: ordered[activeIndex + 1]!.key }); };

  // Tastatur: j/k bzw. Pfeile navigieren, → betritt den Fokus-Modus, ← verlässt
  // ihn. Nicht in Eingabefeldern, nicht bei Tastenkombinationen mit
  // Ctrl/Cmd/Alt (Browser-/OS-Shortcuts), und preventDefault nur dort, wo
  // tatsächlich etwas passiert.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Im Präsentationsmodus gehören die Pfeiltasten der Vorbereitung bzw.
      // dem Presenter-Cockpit (Weiter/Zurück), nicht der Ticketnavigation.
      if (usePresentationStore.getState().phase !== "idle") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable)) return;
      // Menü/Dialog/Schaltflächen: Tastendrücke gehören dem Bedienelement, nicht der
      // Ticketnavigation. `BUTTON` bewusst nicht mehr pauschal blockiert — Ticketzeilen
      // sind `UnstyledButton`, das würde j/k nach einem Zeilenklick lahmlegen.
      if (el && el.closest('[aria-haspopup], [role="menuitem"], [role="dialog"], [role="menu"], [data-menu-dropdown]')) return;
      if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); goNext(); }
      else if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { if (!focusMode) { e.preventDefault(); void patch({ fokus: true }); } }
      else if (e.key === "ArrowLeft") { if (focusMode) { e.preventDefault(); void patch({ fokus: undefined }); } }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, ordered, focusMode]);

  const reauth = isJiraReauthRequired(customers.error) || isJiraReauthRequired(tickets.error);

  // Die Site kommt aus derselben Abfrage wie der Verbindungsstatus (React Query
  // bündelt sie mit der Seite darüber). `siteProblem` ist der Fall, in dem der
  // Lead beim Zustimmen die Site nicht eindeutig gewählt hat — dann steht die
  // Begründung im Banner, nicht nur ein allgemeines „neu verbinden".
  const connection = useCustomerViewConnection();
  const site = connection.data?.site ?? null;
  const siteProblem = connection.data?.siteProblem ?? null;
  const siteProblemCode = connection.data?.siteProblemCode ?? null;
  const configuredHost = connection.data?.configuredHost ?? null;
  // Übersetzt wird über den Code, nicht über den Text: Die Meldung des Servers
  // ist deutsch. Für einen Fall ohne Code (oder einen künftigen, den diese
  // Fassung nicht kennt) bleibt sie der Rückfall — ein englischer Satz mit
  // deutschem Einschub ist besser als gar kein Hinweis.
  const siteProblemText =
    siteProblemCode === "SITE_AMBIGUOUS" || siteProblemCode === "SITE_NONE"
      ? t(`pages.customerView.siteProblem.${siteProblemCode}`, { host: configuredHost ?? "" })
      : (siteProblem ?? undefined);

  // Der Rest des Fensters unter dem Seitenkopf — gemessen, damit Liste und
  // Detail per Flex dorthin reichen und die klebende Kommentar-Fußzeile
  // sichtbar bleibt (`useFillViewport`).
  const fill = useFillViewport<HTMLDivElement>();

  return (
    <Stack
      ref={fill.ref}
      gap="sm"
      // Sobald die Höhe gemessen ist, darf der Stapel NICHT mehr wachsen:
      // Mit `flex: 1` in einem Elternbereich, der nur eine Mindesthöhe hat,
      // gewinnt die Inhaltshöhe gegen das Inline-`height`, und die
      // Scrollbereiche darunter dehnten sich auf 14 000 px (Staging, #254).
      style={{ flex: fill.height === undefined ? 1 : "0 0 auto", minHeight: 0, height: fill.height }}
    >
      {/* `SITE_UNKNOWN` gehört nicht ins Banner: Neu verbinden hilft nicht,
          wenn Atlassian klemmt — dafür steht der graue Badge unten. */}
      {(reauth || (siteProblem !== null && siteProblemCode !== "SITE_UNKNOWN")) && (
        <ReconnectBanner message={siteProblemCode === "SITE_UNKNOWN" ? undefined : siteProblemText} />
      )}
      <Group justify="space-between" wrap="wrap">
        <Box data-print="hide">
          <Group gap="xs" align="center">
            <CustomerPicker
              filter={filter}
              onFilterChange={(f) => void patch({ filter: f, kunde: undefined, ticket: undefined, q: undefined, status: undefined, typ: undefined, ersteller: undefined, bearbeiter: undefined })}
              customers={customers.data ?? []}
              isLoading={customers.isLoading}
              isError={customers.isError && !reauth}
              selectedKey={customerKey}
              disabled={phase !== "idle"}
              onSelect={(k) => { saveLastCustomer(k); void patch({ kunde: k, ticket: undefined, q: undefined, status: undefined, typ: undefined, ersteller: undefined, bearbeiter: undefined }); }}
            />
            {site
              ? <SiteBadge host={site.host} configuredHost={configuredHost} />
              : siteProblemCode === "SITE_UNKNOWN" && <SiteUnknownBadge />}
          </Group>
        </Box>
        <Group gap="sm">
          {customerKey && tickets.data && (
            <Text size="sm" c="dimmed">
              {t("pages.customerView.cockpit.summary", {
                open: result.visible.filter((x) => x.statusCategory !== "done").length,
                total: result.visible.length,
                waiting: result.counts.waiting,
                inProgress: result.counts.inProgress,
              })}
            </Text>
          )}
          {/* `selectedCustomer` ist Bedingung, nicht Zierde: Das Cockpit nimmt
              den Kundennamen für Bühne und Protokoll — ohne geladenen Kunden
              stünde dort eine leere Zeichenkette. */}
          {customerKey && tickets.data && phase === "idle" && result.visible.length > 0 && selectedCustomer !== null && (
            <Button size="xs" data-print="hide" onClick={() =>
                startPrep(
                  ordered.map((x) => x.key),
                  groupingForPrep(result, view.group),
                  ordered.filter((x) => x.steering === true).map((x) => x.key),
                )
              }
            >
              {t("pages.customerView.present.start")}
            </Button>
          )}
        </Group>
      </Group>

      {!customerKey && <Text c="dimmed">{t("pages.customerView.cockpit.pickCustomerHint")}</Text>}
      {customerKey && tickets.isLoading && <Loader size="sm" />}
      {customerKey && tickets.isError && !reauth && (
        <Alert color="red">{tickets.error instanceof ApiError ? tickets.error.message : t("pages.customerView.cockpit.ticketsError")}</Alert>
      )}

      {customerKey && tickets.data && phase !== "idle" && (
        <>
          {(phase === "prep" || phase === "generating") && (
            <PresentPrep
              tickets={ordered}
              customerObjectKey={customerKey}
              customerName={selectedCustomer?.name ?? ""}
              bereichName={selectedCustomer?.bereich?.name ?? null}
            />
          )}
          {/* Ab hier die volle Ticketliste: das Deck steht fest und darf nicht
              an einem nachträglich gesetzten Filter hängen. */}
          {phase === "review" && <PrepReview tickets={tickets.data} customerObjectKey={customerKey} />}
          {phase === "presenting" && (
            <PresenterCockpit
              tickets={tickets.data}
              customerObjectKey={customerKey}
              customerName={selectedCustomer?.name ?? ""}
            />
          )}
        </>
      )}

      {customerKey && tickets.data && phase === "idle" && (
        <>
          {!focusMode && (
            <Paper withBorder p="sm" data-print="hide"><TicketToolbar view={view} onChange={setView} counts={result.counts} /></Paper>
          )}
          <Group align="stretch" gap="md" wrap="nowrap" style={{ flex: 1, minHeight: 0 }}>
            {!focusMode && (
              <Paper withBorder w={340} data-print="hide" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                <Group justify="space-between" px="sm" py={6}>
                  <Text size="sm" fw={600}>{t("pages.customerView.cockpit.tickets")}</Text>
                  <Button size="compact-xs" variant="subtle" onClick={() => void patch({ fokus: true })}>{t("pages.customerView.cockpit.collapse")}</Button>
                </Group>
                <ScrollArea style={{ flex: 1, minHeight: 0 }}>
                  <TicketList
                    visible={result.visible}
                    groups={result.groups}
                    activeKey={activeKey}
                    onSelect={(k) => void patch({ ticket: k })}
                    reviewedKeys={reviewedKeys}
                    newKeys={newKeys}
                    onToggleReviewed={toggleReviewed}
                  />
                </ScrollArea>
              </Paper>
            )}
            <Paper withBorder p="md" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {focusMode && (
                <Box mb="sm" data-print="hide"><Button size="compact-xs" variant="subtle" onClick={() => void patch({ fokus: undefined })}>{t("pages.customerView.cockpit.expand")}</Button></Box>
              )}
              <ScrollArea style={{ flex: 1, minHeight: 0 }} data-print="detail">
                {activeTicket ? (
                  <TicketDetail ticket={activeTicket} focusMode={focusMode} onPrev={goPrev} onNext={goNext} hasPrev={activeIndex > 0} hasNext={activeIndex >= 0 && activeIndex < ordered.length - 1} objectKey={customerKey} />
                ) : (
                  <Text c="dimmed">{t("pages.customerView.cockpit.noSelection")}</Text>
                )}
              </ScrollArea>
            </Paper>
          </Group>
        </>
      )}
    </Stack>
  );
}
